import React, { useMemo, useState } from "react";
import { auth } from "../../firebase";
import { colors, radius, spacing, typography, cardBase } from "../../utils/designTokens";
import { useIsMobile } from "../../hooks/useResponsive";
import { useUser } from "../../context/UserContext";
import { useProspeccionData } from "../../hooks/useProspeccionData";
import { datosEdificio } from "../../utils/datosEdificio";
import { contactosDeCrm, ROL_LABELS } from "../../utils/crmContactos";
import StatusBadge from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import CrmFicha from "../../components/CrmFicha";
import {
  FaRegAddressCard, FaSearch, FaChevronRight, FaPhoneAlt, FaRegEnvelope, FaUserTie, FaWhatsapp,
} from "react-icons/fa";

const telDigits = (t) => String(t || "").replace(/\D/g, "");

/**
 * Directorio de contactos del comercial: todos los contactos cargados en las
 * fichas de sus edificios, vinculados a la dirección y a quién lo gestiona.
 * Buscador por nombre/dirección/teléfono; tocar una fila abre la ficha CRM del
 * edificio para seguir gestionándolo. SuperAdmin ve todos; el comercial, los suyos.
 */
export default function ContactosComercial({ setSelected }) {
  const isMobile = useIsMobile();
  const { userType } = useUser();
  const isSuperAdmin = Number(userType) === 2;
  const email = (auth.currentUser?.email || "").toLowerCase();
  const { puntos, crmByPadron, comerciales, loading, updateCrm } = useProspeccionData();
  const [ficha, setFicha] = useState(null);
  const [busca, setBusca] = useState("");

  const puntoByPadron = useMemo(() => {
    const m = {}; for (const p of puntos) m[String(p.padron)] = p; return m;
  }, [puntos]);

  // Aplana los contactos de las fichas visibles en filas ligadas a su edificio.
  const filas = useMemo(() => {
    const out = [];
    for (const c of Object.values(crmByPadron)) {
      if (!isSuperAdmin && String(c.asignadoEmail || "").toLowerCase() !== email) continue;
      const cs = contactosDeCrm(c);
      if (!cs.length) continue;
      const ef = datosEdificio(puntoByPadron[String(c.padron)], c);
      cs.forEach((ct, idx) => {
        if (!(ct.nombre || ct.tel || ct.email)) return;
        out.push({
          key: `${c.padron}-${idx}`, padron: c.padron,
          direccion: ef.direccion || `Padrón ${c.padron}`,
          nombre: ct.nombre, rol: ct.rol, tel: ct.tel, email: ct.email,
          asignadoA: c.asignadoA || "", estado: c.estado || "sin_contactar",
        });
      });
    }
    out.sort((a, b) => String(a.direccion).localeCompare(String(b.direccion)) || String(a.nombre).localeCompare(String(b.nombre)));
    return out;
  }, [crmByPadron, puntoByPadron, isSuperAdmin, email]);

  const visibles = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return filas;
    return filas.filter((f) =>
      [f.nombre, f.direccion, f.tel, f.email, f.asignadoA].some((x) => String(x || "").toLowerCase().includes(q))
    );
  }, [filas, busca]);

  function abrirFicha(padron) {
    const p = puntoByPadron[String(padron)] || { padron, direccion: crmByPadron[String(padron)]?.direccion || null };
    setFicha(p);
  }

  return (
    <div style={{ padding: isMobile ? spacing[4] : spacing[6], maxWidth: 1100, margin: "0 auto" }}>
      {/* Buscador */}
      <div style={{ display: "flex", alignItems: "center", gap: spacing[2], background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, padding: "0 12px", marginBottom: spacing[4] }}>
        <FaSearch size={13} color={colors.gray400} />
        <input
          value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar contacto, dirección o teléfono…"
          aria-label="Buscar contacto"
          style={{ flex: 1, minWidth: 0, border: "none", outline: "none", padding: "11px 0", fontSize: typography.sizes.base, fontFamily: typography.fontFamily, background: "transparent", color: colors.gray900 }}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: spacing[8], color: colors.gray600 }}>Cargando contactos…</div>
      ) : visibles.length === 0 ? (
        <EmptyState
          icon={FaRegAddressCard}
          title={busca ? "Sin resultados" : "Todavía no cargaste contactos"}
          description={busca ? "Probá con otro nombre o dirección." : "Abrí un edificio, cargá sus contactos en la ficha (presidente, portero, propietario) y aparecen acá."}
        />
      ) : (
        <div style={{ ...cardBase, padding: 0, overflow: "hidden" }}>
          {visibles.map((f, i) => (
            <div
              key={f.key}
              className="habitia-table-row"
              onClick={() => abrirFicha(f.padron)} role="button" tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && abrirFicha(f.padron)}
              style={{
                display: "flex", alignItems: "center", gap: spacing[3],
                padding: `${spacing[3]}px ${spacing[4]}px`, cursor: "pointer",
                borderBottom: i === visibles.length - 1 ? "none" : `1px solid ${colors.gray100}`,
                transition: "background 0.15s ease",
              }}
            >
              <div style={{ width: 36, height: 36, borderRadius: radius.full, background: colors.primaryLighter, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <FaUserTie size={15} color={colors.primary} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: spacing[2], flexWrap: "wrap" }}>
                  <span style={{ fontSize: typography.sizes.md, color: colors.gray900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {f.nombre || "Sin nombre"}
                  </span>
                  {f.rol && <span style={rolChip}>{ROL_LABELS[f.rol] || f.rol}</span>}
                  {f.estado && <StatusBadge status={f.estado} size="sm" />}
                </div>
                <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {f.direccion}{f.asignadoA ? ` · gestiona ${f.asignadoA}` : ""}
                </div>
                {(f.tel || f.email) && (
                  <div style={{ display: "flex", gap: spacing[3], flexWrap: "wrap", marginTop: 2 }}>
                    {f.tel && <span style={{ fontSize: typography.sizes.sm, color: colors.gray600, display: "inline-flex", alignItems: "center", gap: 5 }}><FaPhoneAlt size={9} /> {f.tel}</span>}
                    {f.email && <span style={{ fontSize: typography.sizes.sm, color: colors.gray600, display: "inline-flex", alignItems: "center", gap: 5, minWidth: 0 }}><FaRegEnvelope size={10} /> <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{f.email}</span></span>}
                  </div>
                )}
              </div>
              {/* Atajo directo a WhatsApp del contacto (no abre la ficha) */}
              {telDigits(f.tel) && (
                <a
                  href={`https://wa.me/${telDigits(f.tel).length <= 9 ? "598" + telDigits(f.tel) : telDigits(f.tel)}`}
                  target="_blank" rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`WhatsApp a ${f.nombre}`}
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: radius.sm, color: "#25D366", flexShrink: 0, textDecoration: "none" }}
                >
                  <FaWhatsapp size={16} />
                </a>
              )}
              <FaChevronRight size={11} color={colors.gray300} style={{ flexShrink: 0 }} />
            </div>
          ))}
        </div>
      )}

      <CrmFicha
        open={!!ficha}
        onClose={() => setFicha(null)}
        padron={ficha?.padron}
        punto={ficha}
        crm={ficha ? crmByPadron[String(ficha.padron)] : null}
        comerciales={comerciales}
        onSaved={updateCrm}
        setSelected={setSelected}
      />
    </div>
  );
}

const rolChip = { flexShrink: 0, fontSize: typography.sizes.xs, color: colors.primary, background: colors.primaryLighter, padding: "1px 8px", borderRadius: radius.full };
